import React, { useState, useEffect } from 'react';
import Table from '../../../Table/Table';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';

const ContractorAnalysis = () => {
  const [rawData, setRawData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('Modules');
  const [contractorFilter, setContractorFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [loading, setLoading] = useState(false);

  // Table headers
  const tableHeaders = [
    { dbParam: 'id', displayName: 'Sr. No' },
    { dbParam: 'date', displayName: 'Date' },
    { dbParam: 'action', displayName: 'Action' },
    ...(typeFilter === 'Pallets' ? [{ dbParam: 'count', displayName: 'Module Count' }] : []),
    { dbParam: 'serialNo', displayName: typeFilter === 'Modules' ? 'Module Serial No' : 'Pallet ID' },
    { dbParam: 'contractor', displayName: 'Contractor' },
  ];

  // Filter options
  const actionOptions = ['', 'assign', 'return'];
  const typeOptions = ['Modules', 'Pallets'];
  // Dynamically generate contractor options from rawData
  const contractorOptions = ['', ...new Set(rawData.map(log => log.contractor).filter(contractor => contractor))];

  // Fetch data from API once
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const projectId = localStorage.getItem('selectedProject');
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await axios.post(`${baseURL}/get_contractor_analysis`, { projectId });
        const logs = response.data.data.logs;
        setRawData(logs);
      } catch (error) {
        console.error('Error fetching contractor analysis:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []); // Empty dependency array for one-time fetch

  // Transform and filter data
  useEffect(() => {
    // Transform raw data based on typeFilter
    const transformedData = rawData.flatMap((log) => {
      const items = typeFilter === 'Modules' ? log.modules : log.pallets;
      return items.map((serialNo, itemIndex) => ({
        id: itemIndex + 1,
        date: log.date,
        action: log.action,
        count: log.modules.length, // Only used for pallets
        serialNo: serialNo,
        contractor: log.contractor,
        modules: log.modules,
        pallets: log.pallets,
      }));
    });

    // Apply filters
    let filteredData = transformedData;

    // Date range filter
    if (startDate && endDate) {
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // Action filter
    if (actionFilter) {
      filteredData = filteredData.filter((item) => item.action === actionFilter);
    }

    // Contractor filter
    if (contractorFilter) {
      filteredData = filteredData.filter((item) => item.contractor === contractorFilter);
    }

    // Search filter
    if (searchQuery) {
      filteredData = filteredData.filter((item) =>
        item.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.contractor.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.serialNo.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setTableData(filteredData);
  }, [rawData, typeFilter, startDate, endDate, actionFilter, contractorFilter, searchQuery]);

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    const sortedData = [...tableData].sort((a, b) => {
      if (isAsc) {
        return a[property] < b[property] ? 1 : -1;
      }
      return a[property] > b[property] ? 1 : -1;
    });
    setTableData(sortedData);
  };

  // Download Excel
  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData.map((item) => ({
      'Sr. No': item.id,
      'Date': item.date,
      'Action': item.action,
      ...(typeFilter === 'Pallets' ? { 'Module Count': item.count } : {}),
      [typeFilter === 'Modules' ? 'Module Serial No' : 'Pallet ID']: item.serialNo,
      'Contractor': item.contractor,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contractor Analysis');
    XLSX.writeFile(workbook, 'Contractor_Analysis.xlsx');
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Contractor Analysis</h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <div className="flex gap-2">
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholderText="Start Date"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholderText="End Date"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Action</label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {actionOptions.map((option) => (
              <option key={option} value={option}>
                {option || 'All'}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {typeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Contractor</label>
          <select
            value={contractorFilter}
            onChange={(e) => setContractorFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {contractorOptions.map((option) => (
              <option key={option} value={option}>
                {option || 'All'}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={downloadExcel}
          className="ml-auto bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Download Excel
        </button>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : (
        <Table
          tableHeaders={tableHeaders}
          tableData={tableData}
          order={order}
          orderBy={orderBy}
          handleSort={handleSort}
          renderCell={(header, row) => {
            if (header.displayName === 'Sr. No') {
              return tableData.indexOf(row) + 1; 
            }
            return null; 
          }}
        />
      )}
    </div>
  );
};

export default ContractorAnalysis;